/**
 * The fonts a seller may give their storefront and their product pages: one
 * for headings and one for body text, stored on the page's design map as
 * `design.headingFont` and `design.bodyFont` (src/lib/product-design.ts).
 *
 * WHY A FIXED LIST. A font is a file every visitor downloads, so an open
 * "any Google font" field would let one seller put megabytes, or a face nobody
 * can read, in front of their buyers. These are self-hosted through next/font
 * (src/lib/page-font-faces.ts declares the loaders), so a page asks our own
 * origin for them and nothing reaches Google when it is viewed. All of them are
 * under the SIL Open Font License, which allows that.
 *
 * THE SITE FONT IS NOT IN THE LIST. Unset ('' or absent) means Inter, the font
 * the rest of the site is set in, so "Default" in the pickers is the absence of
 * a choice rather than a stored id. That keeps every design map written before
 * fonts existed meaning exactly what it meant.
 *
 * BODY IS A SUBSET. Every font here may be the heading font. Only the faces
 * that stay readable at paragraph size may be the body font: the display faces
 * (and a monospace) are fine for a title and tiring for a description.
 *
 * ADDING ONE is a web-only change: firestore.rules and the functions repair
 * check the id's FORMAT (PAGE_FONT_ID_PATTERN, at most MAX_PAGE_FONT_ID_LEN
 * characters), not this list. Add the entry here and its loader in
 * page-font-faces.ts; src/lib/page-fonts.test.ts fails until the two agree.
 * REMOVING one is safe for the same reason: resolvePageFont reads an id it does
 * not know as the site font, so a page that used it falls back to Inter.
 *
 * Pure: no next/font, no React. The node test runner loads it.
 */

export type PageFontCategory = 'sans' | 'serif' | 'display' | 'mono';

export type PageFontRole = 'heading' | 'body';

export type PageFont = {
  /** The stored id. Lowercase letters, digits and hyphens. */
  readonly id: string;
  /** The family's name, as the pickers show it. */
  readonly label: string;
  readonly category: PageFontCategory;
  /** Whether it may be the body font. Every font may be the heading font. */
  readonly body: boolean;
};

/**
 * The catalogue, in the order the pickers list it: the text faces that can do
 * both jobs first, then the heading-only ones.
 */
export const PAGE_FONTS = [
  { id: 'dm-sans', label: 'DM Sans', category: 'sans', body: true },
  { id: 'manrope', label: 'Manrope', category: 'sans', body: true },
  { id: 'archivo', label: 'Archivo', category: 'sans', body: true },
  { id: 'atkinson-hyperlegible', label: 'Atkinson Hyperlegible', category: 'sans', body: true },
  { id: 'lora', label: 'Lora', category: 'serif', body: true },
  { id: 'source-serif', label: 'Source Serif', category: 'serif', body: true },
  { id: 'space-grotesk', label: 'Space Grotesk', category: 'sans', body: false },
  { id: 'sora', label: 'Sora', category: 'sans', body: false },
  { id: 'bricolage-grotesque', label: 'Bricolage Grotesque', category: 'display', body: false },
  { id: 'syne', label: 'Syne', category: 'display', body: false },
  { id: 'unbounded', label: 'Unbounded', category: 'display', body: false },
  { id: 'playfair-display', label: 'Playfair Display', category: 'serif', body: false },
  { id: 'fraunces', label: 'Fraunces', category: 'serif', body: false },
  { id: 'jetbrains-mono', label: 'JetBrains Mono', category: 'mono', body: false },
] as const satisfies readonly PageFont[];

export type PageFontId = (typeof PAGE_FONTS)[number]['id'];

/** What the pickers call the unset choice: the site's own font. */
export const SITE_FONT_LABEL = 'Inter';

/**
 * The stored id's bounds, MIRRORED in firestore.rules (designShapeWithinBounds:
 * `size() <= 32 && matches('^[a-z0-9-]*$')`) and in the functions repair
 * (functions/src/lib/user-field-bounds.ts). '' passes: it is the stored "site
 * font". page-fonts.test.ts holds every catalogue id to both.
 */
export const MAX_PAGE_FONT_ID_LEN = 32;
export const PAGE_FONT_ID_PATTERN = /^[a-z0-9-]*$/;

const BY_ID: ReadonlyMap<string, PageFont> = new Map(PAGE_FONTS.map((font) => [font.id, font]));

/**
 * A stored font id as the page should render it: a catalogue id allowed in
 * that role, or null for the site font.
 *
 * The design map is client-written to a world-readable document, so anything
 * else (an id since removed from the catalogue, a heading-only face stored as
 * the body font by a hand-edited document, a non-string) means "site font"
 * rather than reaching a style attribute.
 */
export function resolvePageFont(id: unknown, role: PageFontRole): PageFontId | null {
  if (typeof id !== 'string' || id === '') return null;
  const font = BY_ID.get(id);
  if (!font) return null;
  if (role === 'body' && !font.body) return null;
  return font.id as PageFontId;
}

/** The fonts a picker offers for one role, in catalogue order. */
export function pageFontOptions(role: PageFontRole): readonly PageFont[] {
  return role === 'heading' ? PAGE_FONTS : PAGE_FONTS.filter((font) => font.body);
}

/** A stored id's label, or the site font's for unset and unknown ids. */
export function pageFontLabel(id: unknown, role: PageFontRole): string {
  const resolved = resolvePageFont(id, role);
  return resolved ? BY_ID.get(resolved)!.label : SITE_FONT_LABEL;
}
